/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import {
  getContentId,
  getContentImage,
  getContentTitle,
  useMasterQueue,
} from "@packages/media";
import type { QueueManagedItem } from "@packages/sdk";
import type { JSX } from "react";
import { useContext, useRef, useState } from "react";
import type { DropEvent } from "react-aria";
import { VisuallyHidden } from "react-aria";

import type { ThemeType } from "../../../../../global/emotion";
import {
  constantSemanticColors,
  numericPercentages,
  numericPixels,
  numericValues,
  radius,
  shades,
  spacing,
  stroke,
  ThemeProvider,
  zIndices,
} from "../../../../../global/emotion";
import { IconButton } from "../../../../components/_base/_core/IconButton/IconButton.emotion";
import { QueueIcon } from "../../../../components/_base/icons/Icon/MediaIcons";
import { FullscreenModal } from "../../../../components/_base/modals/FullscreenModal/FullscreenModal.emotion";
import { ModalHeader } from "../../../../components/_base/modals/ModalHeader/ModalHeader.emotion";
import { Loader } from "../../../../components/blocks/other/Loader/Loader.emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import { emotionDynamics } from "../../../../lib/emotionDynamics";
import type { WithButtonWrapper } from "../../../../types/withButtonWrapper.types";
import { colorMix } from "../../../../utils/colors/colorMix";
import { QueueItem } from "../../blocks/data/QueueItem/QueueItem.emotion";
import { QueueControls } from "../../blocks/groups/QueueControls/QueueControls.emotion";
import { FragmentAsChild } from "../../primitives/FragmentAsChild/FragmentAsChild";
import { PlayerInternalContext } from "../MediaPlayer/MediaPlayer.emotion";
import { UIQueue } from "../UIQueue/UIQueue.emotion";

const defaultPlaceholderStyle: Interpolation = {
  borderRadius: radius.full,
  height: numericValues[0],
  outlineColor: constantSemanticColors.onColorHighest,
  outlineStyle: "solid",
  outlineWidth: stroke.regular,
  position: "relative",
  width: numericPercentages[100],
};
const titlePlaceholderStyle: Interpolation = {
  background: constantSemanticColors.onColorHighest,
  color: constantSemanticColors.onColorLowest,
  left: numericPercentages[50],
  padding: spacing.t,
  position: "absolute",
  top: numericPixels[2],
  transform: "translateX(-50%)",
};

const closeModalStyle: Interpolation = {
  marginLeft: "revert",
  marginRight: "auto",
  position: "absolute",
  left: spacing.m,
  top: spacing.m,
  [cssMediaQueryMinWidthTablet]: {
    left: "80px",
    top: "80px",
  },
};
const fontsModalStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
});
const placementModalStyle: Interpolation = {
  margin: "56px auto auto",
};
const zAxisModalStyle: Interpolation = {
  zIndex: zIndices.queueContent,
};

const defaultOverlayStyle: Interpolation = {
  background: shades.shade60,
  display: "none",
  inset: 0,
  placeContent: "center",
  placeItems: "center",
  position: "fixed",
  zIndex: zIndices.loading,
};
const activeOverlayStyle: Interpolation = {
  display: "flex",
};

export type QueueProps = WithButtonWrapper;

export const Queue = ({ Wrapper = FragmentAsChild }: QueueProps) => {
  const t = useTranslations();
  const queueRef = useRef<HTMLDivElement>(null);
  const {
    itemRefs,
    dropProps,
    queue,
    onDragMove,
    dragIndex,
    onDragEnd,
    onDragStart,
    draggedTitle,
  } = useMasterQueue({ queueRef });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const analytics = useAnalytics();
  const { popupRef } = useContext(PlayerInternalContext);

  const colorHex = getContentImage(queue.currentItem)?.color_hex ?? "#000000";

  const Placeholder = ({ title }: { title: string }) => (
    <div css={defaultPlaceholderStyle}>
      <div css={titlePlaceholderStyle}>{title}</div>
    </div>
  );

  const handleDragStart = (title: string) => {
    setIsDragging(true);
    onDragStart(title);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    onDragEnd();
  };

  const past = queue.pastQueue.map((qi) => (
    <QueueItem key={qi.uuid} item={qi} variant={"past"} />
  ));

  const upcoming: JSX.Element[] = [];
  let addedPlaceholder = false;

  for (let i = 0; i < queue.upcomingQueue.length; i++) {
    if (dragIndex === i && draggedTitle) {
      upcoming.push(
        <Placeholder
          key={`dragPlaceholder_${draggedTitle}`}
          title={draggedTitle}
        />,
      );
      addedPlaceholder = true;
    }

    const qi = queue.upcomingQueue[i];
    upcoming.push(
      <QueueItem
        key={qi.uuid}
        ref={itemRefs.current[qi.uuid]}
        item={qi}
        variant={"future"}
        onDragStart={() => {
          analytics.track({ event: HallowAnalyticsEvent.DraggedQueueItem });
          return handleDragStart(getContentTitle(qi));
        }}
        onDragEnd={handleDragEnd}
        onDragMove={onDragMove}
      />,
    );
  }

  if (!addedPlaceholder && dragIndex !== null && draggedTitle !== null) {
    upcoming.push(
      <Placeholder
        key={`dragPlaceholder_${draggedTitle}`}
        title={draggedTitle}
      />,
    );
  }

  const current = queue.currentItem ? (
    <QueueItem
      key={queue.currentItem?.uuid ?? "currentItem"}
      item={queue.currentItem}
      variant={"current"}
    />
  ) : null;

  if (queue.repeating === "all") {
    upcoming.push(
      ...queue.pastQueue.map((qi) => {
        return (
          <QueueItem variant={"repeat"} item={qi} key={`repeat_${qi.uuid}`} />
        );
      }),
    );
    if (queue.currentItem)
      upcoming.push(
        <QueueItem
          variant={"repeat"}
          item={queue.currentItem}
          key={`repeat_${queue.currentItem.uuid}`}
        />,
      );
    upcoming.push(
      ...queue.upcomingQueue.map((qi) => {
        return (
          <QueueItem variant={"repeat"} item={qi} key={`repeat_${qi.uuid}`} />
        );
      }),
    );
  }

  const handleDelete = async (e: DropEvent) => {
    const itm = e.items[0];
    if (itm.kind === "text") {
      const txt = await itm.getText("text/json");
      const obj = JSON.parse(txt) as QueueManagedItem;
      queue.delete(obj.uuid);
    }
  };

  return (
    <ThemeProvider forceTheme="dark" as={null}>
      <FullscreenModal
        trigger={
          <Wrapper asChild>
            <IconButton
              icon={<QueueIcon fill={constantSemanticColors.onColorVeryHigh} />}
              variant={"transparentWhite"}
              onClickCapture={() =>
                analytics.track({
                  event: HallowAnalyticsEvent.TappedQueue,
                  properties: {
                    enabled: true,
                    content_id: getContentId(queue.currentItem),
                    content_type: queue.currentItem?.type,
                  },
                })
              }
              tabIndex={0}
            />
          </Wrapper>
        }
        contentCSS={[
          fontsModalStyle,
          zAxisModalStyle,
          emotionDynamics.colorMixBackgroundColor({
            source: colorHex,
            adjustment: shades.shade30,
          }),
        ]}
        closeCSS={closeModalStyle}
        closeProps={{ variant: "transparentWhite" }}
        portalProps={{ container: popupRef.current }}
      >
        <VisuallyHidden>
          <ModalHeader>{t("accessibility_label_queue_button")}</ModalHeader>
        </VisuallyHidden>
        <div
          css={[defaultOverlayStyle, queue.loading ? activeOverlayStyle : null]}
        >
          <Loader />
        </div>
        <UIQueue
          ref={queueRef}
          past={{ title: t("recently_played"), items: past }}
          future={{ title: t("playing_next"), items: upcoming }}
          current={current}
          isDragging={isDragging}
          onDelete={handleDelete}
          css={placementModalStyle}
          titleBgCSS={[
            emotionDynamics.colorMixBackgroundColor({
              source: colorHex,
              adjustment: shades.shade30,
            }),
          ]}
          masksColor={
            colorMix<"string">({
              source: colorHex,
              adjustment: shades.shade30,
              format: "string",
            }) as `rgba(${number},${number},${number},${number})`
          }
          {...dropProps}
        >
          <QueueControls Wrapper={Wrapper} />
        </UIQueue>
      </FullscreenModal>
    </ThemeProvider>
  );
};
